import { lazy, useState, createElement, type ComponentType } from "react";

/**
 * A code-split page that stops being code-split once its module is in hand.
 *
 * `React.lazy` alone cannot do this. Its payload is resolved by RENDERING it:
 * the first render calls the loader, throws the promise, and the nearest
 * Suspense boundary commits its fallback — and it does that even when the
 * module was fetched minutes ago and is sitting in memory, because a warmed
 * module and a resolved payload are different things. So prefetching the chunk
 * removes the network wait and leaves behind the artefact it was meant to
 * remove: a first visit that paints a fallback, swaps in the page, and starts
 * the page's entrance animation a frame after the browser has already shown
 * the finished layout. Every LATER visit is clean, because by then the payload
 * is resolved — which is exactly why the first one looks wrong and nothing
 * else does.
 *
 * This keeps the module itself, not just the promise. Once `preload()` has
 * settled, the component renders the real thing synchronously: no throw, no
 * boundary, no fallback commit, and the page's own entrance runs on its own
 * mount the way it does on every subsequent visit. Until then it is an
 * ordinary `React.lazy` and behaves exactly as it did, so a reader who clicks
 * during the warm-up still gets a working page and a fallback rather than a
 * blank one.
 *
 * The choice is pinned per mount with `useState`'s initialiser: if the warm-up
 * lands while a lazily-mounted instance is on screen, swapping the element type
 * underneath it would unmount and remount a page the reader is already reading.
 * Whichever path an instance starts on, it finishes on.
 *
 * `preload` is idempotent and safe to call from anywhere — it shares one loader
 * with `lazy`, so the two share one fetch and one module evaluation. A rejected
 * load clears the memo, so a later navigation retries and fails where the
 * reader is actually waiting for something rather than caching the failure in
 * silence.
 *
 * Deliberately props-less: every caller is a routed page or a tab panel, which
 * React renders with no props of its own. Keeping the signature that narrow is
 * what lets the resolved and unresolved branches share one type.
 */
export type WarmLazyComponent = ComponentType<Record<string, never>> & {
  /** Fetch the module now. Idempotent; resolves once the module is usable. */
  preload: () => Promise<void>;
};

export function warmLazy(load: () => Promise<{ default: ComponentType }>, displayName?: string): WarmLazyComponent {
  let loaded: ComponentType | null = null;
  let inFlight: Promise<void> | null = null;

  const preload = (): Promise<void> => {
    inFlight ??= load().then(
      (mod) => {
        loaded = mod.default;
      },
      (err: unknown) => {
        inFlight = null;
        throw err;
      },
    );
    return inFlight;
  };

  const Suspending = lazy(load) as unknown as ComponentType;

  const Warm = () => {
    // Boxed, because a lazy `useState` initialiser and a component are both
    // functions: handing React a bare component type would have it call the
    // component as the initialiser.
    const [{ Chosen }] = useState(() => ({ Chosen: loaded ?? Suspending }));
    return createElement(Chosen);
  };
  Warm.preload = preload;
  Warm.displayName = displayName ? `WarmLazy(${displayName})` : "WarmLazy";

  return Warm as WarmLazyComponent;
}
